import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { WizardStepper } from '../components/wizard/WizardStepper';
import { StepBasicInfo } from '../components/wizard/StepBasicInfo';
import { StepProductSelection } from '../components/wizard/StepProductSelection';
import { StepReview } from '../components/wizard/StepReview';
import jobService from '../services/jobService';
import useAuthStore from '../stores/authStore';
import { useConfirm } from '../hooks/useConfirm';

const STEPS = [
  { id: 1, name: 'Informações Básicas', description: 'Hospital, local e responsável' },
  { id: 2, name: 'Produtos', description: 'Selecione os produtos a contar' },
  { id: 3, name: 'Revisão', description: 'Confirme os dados do job' },
];

/**
 * JobWizardPage Component
 * Multi-step wizard for creating new counting jobs
 */
export function JobWizardPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { confirm, ConfirmDialog } = useConfirm();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Form data state
  const [formData, setFormData] = useState({
    hospitalId: null,
    locationId: null,
    titulo: '',
    descricao: '',
    dataPrevista: '',
    productIds: [],
  });

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCancel = async () => {
    const confirmed = await confirm({
      title: 'Cancelar Criação',
      message: 'Tem certeza que deseja cancelar? Todos os dados preenchidos serão perdidos.',
      variant: 'warning',
      confirmText: 'Sim, Cancelar',
    });

    if (confirmed) {
      navigate('/dashboard');
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Validate all data before submission
      if (!formData.hospitalId || !formData.locationId) {
        throw new Error('Dados obrigatórios faltando');
      }

      if (formData.productIds.length === 0) {
        throw new Error('Selecione ao menos um produto');
      }

      // Create job via API
      const newJob = await jobService.createJob({
        hospital_id: formData.hospitalId,
        location_id: formData.locationId,
        operador_id: user?.id,
        titulo: formData.titulo,
        descricao: formData.descricao,
        data_prevista: formData.dataPrevista,
        product_ids: formData.productIds,
      });

      // Success: redirect to dashboard with success message
      navigate('/dashboard', {
        state: {
          message: `Job "${formData.titulo}" criado com sucesso! (ID: ${newJob.id})`,
          type: 'success'
        },
      });
    } catch (error) {
      console.error('Failed to create job:', error);

      if (error.response?.status === 400) {
        setSubmitError(
          error.response.data.detail || 'Dados inválidos. Verifique os campos.'
        );
      } else if (error.code === 'ERR_NETWORK') {
        setSubmitError('Erro de conexão. Verifique sua internet.');
      } else {
        setSubmitError(error.message || 'Erro ao criar job. Tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  return (
    <>
      <ConfirmDialog />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8 px-4">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>

      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-xl mb-4">
            <CheckCircleIcon className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Criar Novo Job de Contagem
          </h1>
          <p className="text-gray-600">
            Preencha as informações para criar um job de inventário
          </p>
        </div>

        {/* Wizard Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100">
          {/* Stepper */}
          <WizardStepper steps={STEPS} currentStep={currentStep} />

          {/* Step Content */}
          <div className="p-8">
            {submitError && (
              <div className="mb-6 bg-red-50 border-l-4 border-red-500 rounded-md p-4">
                <div className="flex items-start">
                  <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5 mr-3" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">{submitError}</p>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <StepBasicInfo
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
              />
            )}

            {currentStep === 2 && (
              <StepProductSelection
                formData={formData}
                updateFormData={updateFormData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}

            {currentStep === 3 && (
              <StepReview
                formData={formData}
                onBack={handleBack}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>

          {/* Navigation Footer */}
          <div className="bg-gray-50 px-8 py-4 flex items-center justify-between border-t border-gray-200">
            <button
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="text-gray-600 hover:text-gray-800 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>

            <div className="text-sm text-gray-500 font-medium">
              Etapa {currentStep} de {STEPS.length}
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Precisa de ajuda? Entre em contato com o suporte em{' '}
            <a href="mailto:suporte@logmed.com" className="text-blue-600 hover:text-blue-700 font-medium">
              suporte@logmed.com
            </a>
          </p>
        </div>
      </div>

        <style dangerouslySetInnerHTML={{
          __html: `
            .bg-grid-pattern {
              background-image:
                linear-gradient(to right, rgba(0,0,0,0.03) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0,0,0,0.03) 1px, transparent 1px);
              background-size: 20px 20px;
            }
          `
        }} />
      </div>
    </>
  );
}
